import { useEffect, useId } from 'react';
import { HotkeyCallback, useHotkeys } from 'react-hotkeys-hook';

export type CommandAction = {
  name: string;
  hotkey?: string;
  disabled?: boolean;
  action: () => void;
}

const registry = new Map<string, CommandAction[]>();

export function getCommandActions(): CommandAction[] {
  return Array.from(registry.values()).reverse().flat().filter(action => !action.disabled);
}

const warnedDuplicates = new Set<string>();

function warnOnDuplicateHotkeys() {
  const seen = new Set<string>();
  for (const action of getCommandActions()) {
    if (!action.hotkey) continue;
    const message = `useCommandActions: hotkey "${action.hotkey}" ("${action.name}") is registered by more than one mounted view - all of them will run`;
    if (seen.has(action.hotkey) && !warnedDuplicates.has(message)) {
      warnedDuplicates.add(message);
      console.warn(message);
    }
    seen.add(action.hotkey);
  }
}

export function formatHotkey(hotkey: string) {
  return hotkey.split('>')
    .map(combo => combo.split('+').map(key => key.charAt(0).toUpperCase() + key.slice(1)).join('+'))
    .join(' then ');
}

export function isTypingTarget(event: KeyboardEvent) {
  return event.target instanceof HTMLElement && (
    event.target.tagName === "INPUT"
    || event.target.tagName === "SELECT"
    || event.target.tagName === "TEXTAREA"
    || event.target.contentEditable === "true"
    || event.target.className.includes('k-dropdownlist'));
}

// This is to prevent unsaved changes from being lost
export function isDialogOpen() {
  return document.querySelector('.k-dialog, .k-window') !== null;
}

// While a "g" navigation sequence is pending, bare-letter actions must not fire
let sequenceStartedAt = 0;

export function markSequenceStart() {
  sequenceStartedAt = Date.now();
}

export function clearSequence() {
  sequenceStartedAt = 0;
}

export function isSequencePending() {
  return Date.now() - sequenceStartedAt < 1000;
}

export function useCommandActions(actions: CommandAction[]) {
  const id = useId();

  useEffect(() => {
    registry.set(id, actions);
    if (import.meta.env.DEV) {
      warnOnDuplicateHotkeys();
    }
  });

  useEffect(() => {
    return () => {
      registry.delete(id);
    };
  }, [id]);

  const enabledActions = actions.filter(a => a.hotkey && !a.disabled);
  // Modifier shortcuts (ctrl+s) fire even while typing in a field; bare keys (n) must not.
  // Shift-only combos count as bare - shift+n is how you type a capital N.
  const hasRealModifier = (hotkey: string) => hotkey.split('+').some(key => ['ctrl', 'control', 'meta', 'alt', 'mod'].includes(key));
  const modifierHotkeys = enabledActions.filter(a => hasRealModifier(a.hotkey)).map(a => a.hotkey);
  const bareHotkeys = enabledActions.filter(a => !hasRealModifier(a.hotkey)).map(a => a.hotkey);

  const dispatch: HotkeyCallback = (e, hotkeysEvent) => {
    const action = actions.find(a => a.hotkey === hotkeysEvent.hotkey && !a.disabled);
    if (action) {
      e.preventDefault();
      action.action();
    }
  };

  const ignoreEventWhen = (e: KeyboardEvent) => {
    // This fixes pressing "n" to filter the book user list
    return (e.target instanceof HTMLElement && e.target.className.includes('k-dropdownlist'));
  };

  useHotkeys(modifierHotkeys, dispatch, { enabled: modifierHotkeys.length > 0, enableOnFormTags: true }, [actions]);
  useHotkeys(bareHotkeys, (e, hotkeysEvent) => {
    if (isSequencePending() || isDialogOpen()) return;
    dispatch(e, hotkeysEvent);
  }, { enabled: bareHotkeys.length > 0, ignoreEventWhen }, [actions]);
}
